import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { IBancoVanBancaria, IContasEPlanilhaResponse } from "../../interfaces";
import { IOrgao, IOrgaoVinculado } from "../../Orgaos/interfaces";
import { IGetUsuarios, IUpdateCategoriasUsuario } from "./Tabs/PermissoesUsuarios/interfaces";
import { IUpdateSistema } from "./Tabs/IntegracaoComSistemaContabil/components/GerenciarSistemaContabil/interfaces";
import { IUpdateSistemaContabilIntegrado, IUpdateTipoEmpenhosERestos } from "./Tabs/IntegracaoComSistemaContabil/components/interfaces";
import { ICreateOrgaoVinculado, IDeleteOrgaoVinculado, IUpdateOrgaoVinculado } from "./Tabs/IntegracaoComSistemaContabil/components/OrgaosVinculadosStep/interfaces";
import { IAutorizacao, IConfiguracaoContabil, IContaSaldoDeContas, IFonteSaldoDeContas, IOrgaoAutorizacaoItem, IOrgaoComErro, ISistemaContabil, ISolicitacaoAtivacao, IUsuarioSolicitacao } from "../interfaces";
import { ICreateConfiguracaoSistemaContabil, IDeleteConfiguracaoSistemaContabil, IUpdateConfiguracaoSistemaContabil } from "./Tabs/IntegracaoComSistemaContabil/components/ConfigurarSistemaContabil/interfaces";
import { ICreateCredenciais, ICreateCredenciaisVan, ICreateIdAplicacao, IDeleteAutorizacao, IDescobertaCnpjContaResponse, IGetAutorizacoesBancoDoBrasil, IGetBancosVanBancaria, IGetContaById, IGetContasComErro, IGetContasInativas, IGetDescobertaCnpjContaComErro, IGetDocumentoVanSignedUrl, IUpdateCredenciais, IUpdateCredenciaisVan, IUpdateIdAplicacao, IUpdateNumeroRequerimentoVan } from "./Tabs/IntegracaoComBancos/interfaces";
import { ICreateConta, ICreateFonte, IDeleteConta, IDeleteFonte, IGetFontes, IGetFontesOrfas, IGetOrdenacaoFontes, IGetOrgaosVinculados, IOrdenacaoCategoriaItem, IUpdateCategoriaFonte, IUpdateConta, IUpdateContasFontes, IUpdateDescricaoFonte, IUpdateFonteFavorita, IUpdateOrdenacaoFontes } from "./Tabs/AlinhamentoDeContasEFontes/interfaces";

import { IntegracaoComSistemaContabil } from "./Tabs/IntegracaoComSistemaContabil";
import { AlinhamentoDeContasEFontes } from "./Tabs/AlinhamentoDeContasEFontes";
import SolicitacoesDeAtivacaoService from "../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import { IntegracaoComBancos } from "./Tabs/IntegracaoComBancos";
import { PermissoesUsuarios } from "./Tabs/PermissoesUsuarios";
import EtapaTabTitle from "./components/EtapaTabTitle";
import { EmitirErro } from "../../../../services/EmitirErro";

export const useGerenciar = () => {
    let { id }: any = useParams();
    const history = useHistory();

    const [solicitacao, setSolicitacao] = useState<ISolicitacaoAtivacao | null>(null);

    const [activeKeyTabs, setActiveKeyTabs] = useState<any>('integracao-contabil');
    const [isLoadingSolicitacao, setIsLoadingSolicitacao] = useState<boolean>(false);
    const [recarregarContas, setRecarregarContas] = useState<boolean>(false);

    const valueDataConclusao = useMemo(() => {
        if (solicitacao?.concluidaEm) {
            return solicitacao.concluidaEm;
        }

        const ultimaEtapaEstaConcluida = solicitacao?.etapasSolicitacaoAtivacao.sort((a, b) => b.etapa.ordem - a.etapa.ordem)[0];

        return ultimaEtapaEstaConcluida?.concluidaEm;
    }, [solicitacao]);

    const onClickVoltarListagem = useCallback((): void => {
        history.push("/solicitacoes-de-ativacao");
    }, [history]);

    const onClickTab = useCallback((tab: string) => {
        setActiveKeyTabs(tab)
    }, []);

    const handleUpdateOrgao = useCallback((orgao: IOrgao) => {
        setSolicitacao((prevState) => {
            if (!prevState) {
                return null;
            }

            return {
                ...prevState,
                orgao,
            }
        });
    }, []);

    const handleUpdateSistemaContabil = useCallback((sistemaContabil: ISistemaContabil) => {
        setSolicitacao((prevState) => {
            if (!prevState) {
                return null;
            }

            return {
                ...prevState,
                sistemaContabil,
                sistemaContabilId: sistemaContabil.id,
            }
        });
    }, []);

    const handleEtapaIniciada = useCallback((solicitacao: ISolicitacaoAtivacao) => {
        setSolicitacao((prevState) => {
            if (!prevState) {
                return null;
            }

            return {
                ...prevState,
                etapasSolicitacaoAtivacao: solicitacao.etapasSolicitacaoAtivacao,
            }
        });
    }, []);

    const handleTipoEmpenhosERestosChange = useCallback((value: string) => {
        setSolicitacao((prevState) => {
            if (!prevState) {
                return null;
            }

            return {
                ...prevState,
                tipoEmpenhosERestos: value,
            }
        });
    }, []);

    const handleSistemaContabilIntegradoChange = useCallback((value: boolean) => {
        setSolicitacao((prevState) => {
            if (!prevState) {
                return null;
            }

            return {
                ...prevState,
                empenhosERestos: value,
            }
        });
    }, []);

    const handleConfiguracaoSistemaContabilChange = useCallback((configuracaoContabil: IConfiguracaoContabil | null) => {
        setSolicitacao((prevState) => {
            if (!prevState) {
                return null;
            }

            return {
                ...prevState,
                configuracaoContabil,
            }
        });
    }, []);

    const handleCreateOrgaoVinculado = useCallback(async (data: ICreateOrgaoVinculado): Promise<IOrgao> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.createOrgaoVinculado(data);

        return updatedSolicitacao.orgao;
    }, []);

    const handleUpdateOrgaoVinculado = useCallback(async (data: IUpdateOrgaoVinculado): Promise<IOrgao> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.updateOrgaoVinculado(data);

        return updatedSolicitacao.orgao;
    }, []);

    const handleDeleteOrgaoVinculado = useCallback(async (data: IDeleteOrgaoVinculado): Promise<IOrgao> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.deleteOrgaoVinculado(data);

        return updatedSolicitacao.orgao;
    }, []);

    const requestUpdateSistemaContabil = useCallback(async (data: IUpdateSistema): Promise<ISistemaContabil> => {
        const result = await SolicitacoesDeAtivacaoService.updateSistemaContabil(data);

        return result.sistemaContabil;
    }, []);

    const handleCreateConfiguracaoSistemaContabil = useCallback(async (data: ICreateConfiguracaoSistemaContabil): Promise<IConfiguracaoContabil | null> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.createConfiguracaoSistemaContabil(data);

        return updatedSolicitacao.configuracaoContabil;
    }, []);

    const handleUpdateConfiguracaoSistemaContabil = useCallback(async (data: IUpdateConfiguracaoSistemaContabil): Promise<IConfiguracaoContabil | null> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.updateConfiguracaoSistemaContabil(data);

        return updatedSolicitacao.configuracaoContabil;
    }, []);

    const handleDeleteConfiguracaoSistemaContabil = useCallback(async (data: IDeleteConfiguracaoSistemaContabil): Promise<void> => {
        await SolicitacoesDeAtivacaoService.deleteConfiguracaoSistemaContabil(data.solicitacaoId);
    }, []);

    const handleUpdateTipoEmpenhosERestos = useCallback(async (data: IUpdateTipoEmpenhosERestos): Promise<string> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.updateTipoEmpenhosERestos(data);

        return updatedSolicitacao.tipoEmpenhosERestos;
    }, []);

    const handleUpdateSistemaContabilIntegrado = useCallback(async (data: IUpdateSistemaContabilIntegrado): Promise<boolean> => {
        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.updateSistemaContabilIntegrado(data);

        return updatedSolicitacao.empenhosERestos;
    }, []);

    const handleGetOrdenacaoFontes = useCallback(async (data: IGetOrdenacaoFontes): Promise<IOrdenacaoCategoriaItem[]> => {
        const ordenacaoFontesData = await SolicitacoesDeAtivacaoService.getOrdenacaoFontes(data);

        return ordenacaoFontesData;
    }, []);

    const handleGetFontesOrfas = useCallback(async (data: IGetFontesOrfas): Promise<IOrdenacaoCategoriaItem[]> => {
        const id = data.solicitacaoId || solicitacao?.id;
        if (!id) {
            return [];
        }

        const fontesOrfasData = await SolicitacoesDeAtivacaoService.getFontesOrfas({ solicitacaoId: id });

        return fontesOrfasData;
    }, [solicitacao]);

    const handleGetContasEPlanilha = useCallback(async (data: IGetOrdenacaoFontes): Promise<IContasEPlanilhaResponse> => {
        const contasEPlanilhaData = await SolicitacoesDeAtivacaoService.getContasEPlanilha(data);

        return contasEPlanilhaData;
    }, []);

    const handleUpdateFonteFavorita = useCallback(async (data: IUpdateFonteFavorita): Promise<void> => {
        await SolicitacoesDeAtivacaoService.atualizarFonteFavorita(data);
    }, []);

    const handleUpdateOrdenacaoFontes = useCallback(async (data: IUpdateOrdenacaoFontes): Promise<IOrdenacaoCategoriaItem | null> => {
        const response = await SolicitacoesDeAtivacaoService.atualizarOrdenacaoFontes(data);

        return response;
    }, []);

    const handleUpdateCategoriaFonte = useCallback(async (data: IUpdateCategoriaFonte): Promise<void> => {
        await SolicitacoesDeAtivacaoService.updateCategoriaFonte(data);
    }, []);

    const handleUpdateDescricaoFonte = useCallback(async (data: IUpdateDescricaoFonte): Promise<IFonteSaldoDeContas | null> => {
        if (data.isGroup) {
            const updatedFonte = await SolicitacoesDeAtivacaoService.updateDescricaoFonteConsolidada({
                solicitacaoId: data.solicitacaoId,
                descricao: data.descricao,
                numero: data.numero,
            });

            return updatedFonte;
        }

        const updatedFonte = await SolicitacoesDeAtivacaoService.updateDescricaoFonte({
            solicitacaoId: data.solicitacaoId,
            descricao: data.descricao,
            numero: data.numero,
        });

        return updatedFonte;
    }, []);

    const handleCreateFonte = useCallback(async (data: ICreateFonte): Promise<IFonteSaldoDeContas | null> => {
        const createdFonte = await SolicitacoesDeAtivacaoService.createFonte(data);

        return createdFonte;
    }, []);

    const handleUpdateConta = useCallback(async (data: IUpdateConta): Promise<IContaSaldoDeContas | null> => {
        const updatedContaResponse = await SolicitacoesDeAtivacaoService.updateConta(data);

        return updatedContaResponse;
    }, []);

    const handleCreateConta = useCallback(async (data: ICreateConta): Promise<IContaSaldoDeContas | null> => {
        const createdContaResponse = await SolicitacoesDeAtivacaoService.createConta(data);

        return createdContaResponse;
    }, []);

    const handleDeleteConta = useCallback(async (data: IDeleteConta): Promise<void> => {
        await SolicitacoesDeAtivacaoService.deleteConta(data);
    }, []);

    const handleEncerrarConta = useCallback(async (contaId: number): Promise<void> => {
        await SolicitacoesDeAtivacaoService.encerrarConta(contaId);
    }, []);

    const handleDeleteFonte = useCallback(async (data: IDeleteFonte): Promise<void> => {
        const solicitacaoId = data.solicitacaoId || solicitacao?.id;
        if (!solicitacaoId) {
            return;
        }

        await SolicitacoesDeAtivacaoService.deleteFonte({
            solicitacaoId,
            fonteId: data.fonteId,
        });
    }, [solicitacao]);

    const handleUpdateContasFontes = useCallback(async (data: IUpdateContasFontes): Promise<IContaSaldoDeContas[]> => {
        const response = await SolicitacoesDeAtivacaoService.updateContasFontes({
            solicitacaoId: data.solicitacaoId!,
            contaIds: data.contaIds,
            fonteIds: data.fonteIds,
        });

        return response;
    }, []);

    const handleGetAutorizacoesBancoDoBrasil = useCallback(async (data: IGetAutorizacoesBancoDoBrasil): Promise<IOrgaoAutorizacaoItem[]> => {
        const response = await SolicitacoesDeAtivacaoService.getAutorizacoesBancoDoBrasil(data);

        return response;
    }, []);

    const handleGetContasComErro = useCallback(async (data: IGetContasComErro): Promise<IOrgaoComErro[]> => {
        const response = await SolicitacoesDeAtivacaoService.getContasComErro(data);

        return response;
    }, []);

    const handleGetContasInativas = useCallback(async (data: IGetContasInativas): Promise<IOrgaoComErro[]> => {
        const response = await SolicitacoesDeAtivacaoService.getContasInativas(data);

        return response;
    }, []);

    const handleCreateIdAplicacao = useCallback(async (data: ICreateIdAplicacao): Promise<IAutorizacao | null> => {
        const createdIdAplicacaoResponse = await SolicitacoesDeAtivacaoService.createIdAplicacao(data);

        return createdIdAplicacaoResponse;
    }, []);

    const handleUpdateIdAplicacao = useCallback(async (data: IUpdateIdAplicacao): Promise<IAutorizacao | null> => {
        const updatedIdAplicacaoResponse = await SolicitacoesDeAtivacaoService.updateIdAplicacao(data);

        return updatedIdAplicacaoResponse;
    }, []);

    const handleCreateCredenciais = useCallback(async (data: ICreateCredenciais): Promise<IAutorizacao | null> => {
        const createdCredenciaisResponse = await SolicitacoesDeAtivacaoService.createCredenciais(data);

        return createdCredenciaisResponse;
    }, []);

    const handleUpdateCredenciais = useCallback(async (data: IUpdateCredenciais): Promise<IAutorizacao | null> => {
        const updatedCredenciaisResponse = await SolicitacoesDeAtivacaoService.updateCredenciais(data);

        return updatedCredenciaisResponse;
    }, []);

    const handleDeleteAutorizacao = useCallback(async (data: IDeleteAutorizacao): Promise<void> => {
        await SolicitacoesDeAtivacaoService.deleteAutorizacao(data);
    }, []);

    const handleGetBancosVanBancaria = useCallback(async (data: IGetBancosVanBancaria): Promise<IBancoVanBancaria[]> => {
        if (!data.solicitacaoId) return [];
        const response = await SolicitacoesDeAtivacaoService.getBancosVanBancaria({ solicitacaoId: data.solicitacaoId });
        return response;
    }, []);

    const handleUpdateNumeroRequerimentoVan = useCallback(async (data: IUpdateNumeroRequerimentoVan): Promise<IBancoVanBancaria | null> => {
        if (!data.solicitacaoId) {
            return null;
        }

        const response = await SolicitacoesDeAtivacaoService.updateNumeroRequerimento({
            solicitacaoId: data.solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            numeroRequerimento: data.numeroRequerimento,
        });
        return response;
    }, []);

    const handleCreateCredenciaisVan = useCallback(async (data: ICreateCredenciaisVan): Promise<IBancoVanBancaria | null> => {
        if (!data.solicitacaoId) {
            return null;
        }

        const response = await SolicitacoesDeAtivacaoService.createCredenciaisVanBancaria({
            solicitacaoId: data.solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
        });
        return response;
    }, []);

    const handleUpdateCredenciaisVan = useCallback(async (data: IUpdateCredenciaisVan): Promise<IBancoVanBancaria | null> => {
        if (!data.solicitacaoId) {
            return null;
        }

        const response = await SolicitacoesDeAtivacaoService.updateCredenciaisVanBancaria({
            solicitacaoId: data.solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
        });
        return response;
    }, []);

    const handleGetDocumentoSignedUrlVan = useCallback(async (data: IGetDocumentoVanSignedUrl): Promise<{ url?: string; link?: string } | any> => {
        if (!data.solicitacaoId) {
            return null;
        }

        const response = await SolicitacoesDeAtivacaoService.getDocumentoVanBancariaSignedUrl({
            solicitacaoId: data.solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            documentoId: data.documentoId,
        });
        return response;
    }, []);

    const handleDescobreCnpjContaComErro = useCallback(async (data: IGetDescobertaCnpjContaComErro): Promise<IDescobertaCnpjContaResponse> => {
        const response = await SolicitacoesDeAtivacaoService.descobreCnpjContaComErro(data);

        return response;
    }, []);

    const handleGetContaById = useCallback(async (data: IGetContaById): Promise<IContaSaldoDeContas> => {
        const response = await SolicitacoesDeAtivacaoService.getContaById(data);

        return response;
    }, []);

    const handleContaCorrigida = useCallback(() => {
        setRecarregarContas(true);
    }, []);

    const handleContasRecarregadas = useCallback(() => {
        setRecarregarContas(false);
    }, []);

    const handleGetUsuarios = useCallback(async (data: IGetUsuarios): Promise<IUsuarioSolicitacao[]> => {
        const response = await SolicitacoesDeAtivacaoService.getUsuariosSolicitacao(data);

        return response;
    }, []);

    const handleUpdateCategoriasUsuario = useCallback(async (data: IUpdateCategoriasUsuario): Promise<IUsuarioSolicitacao | null> => {
        const response = await SolicitacoesDeAtivacaoService.updateCategoriasUsuarioSolicitacao(data);

        return response;
    }, []);

    const handleGetFontes = useCallback(async (data: IGetFontes): Promise<IFonteSaldoDeContas[] | null> => {
        if (!data.solicitacaoId) {
            return null;
        }

        const response = await SolicitacoesDeAtivacaoService.getFontes({ solicitacaoId: data.solicitacaoId });

        return response;
    }, []);

    const handleGetOrgaosVinculados = useCallback(async (data: IGetOrgaosVinculados): Promise<IOrgaoVinculado[] | null> => {
        if (!data.solicitacaoId) {
            return null;
        }

        const response = await SolicitacoesDeAtivacaoService.getOrgaosVinculadosBySolicitacaoId({ solicitacaoId: data.solicitacaoId });

        return response;
    }, []);

    const tabs: Array<{
        key: string;
        ordem: number;
        // permissao: boolean;
        label: ReactNode;
        children: JSX.Element;
        disabled?: boolean;
    }> = useMemo(() => {
        return [
            {
                key: 'integracao-contabil',
                ordem: 1,
                label: (
                    <EtapaTabTitle
                        numeroEtapa={1}
                        descricaoEtapa="Integração com sistema contábil"
                        solicitacao={solicitacao}
                    />
                ),
                children:
                    <>
                        <IntegracaoComSistemaContabil
                            solicitacao={solicitacao}
                            orgao={solicitacao?.orgao || null}
                            onEtapaIniciada={handleEtapaIniciada}
                            onEtapaConcluida={handleEtapaIniciada}
                            onUpdateObservacao={handleEtapaIniciada}
                            onUpdateSistemaContabil={handleUpdateSistemaContabil}
                            onSistemaContabilIntegradoChange={handleSistemaContabilIntegradoChange}
                            onUpdateOrgao={handleUpdateOrgao}
                            onTipoEmpenhosERestosChange={handleTipoEmpenhosERestosChange}
                            onConfiguracaoSistemaContabilChange={handleConfiguracaoSistemaContabilChange}
                            onCreateOrgaoVinculado={handleCreateOrgaoVinculado}
                            onUpdateOrgaoVinculado={handleUpdateOrgaoVinculado}
                            onDeleteOrgaoVinculado={handleDeleteOrgaoVinculado}
                            requestUpdateSistemaContabil={requestUpdateSistemaContabil}
                            onCreateConfiguracaoSistemaContabil={handleCreateConfiguracaoSistemaContabil}
                            onUpdateConfiguracaoSistemaContabil={handleUpdateConfiguracaoSistemaContabil}
                            onDeleteConfiguracaoSistemaContabil={handleDeleteConfiguracaoSistemaContabil}
                            onUpdateTipoEmpenhosERestos={handleUpdateTipoEmpenhosERestos}
                            onUpdateSistemaContabilIntegrado={handleUpdateSistemaContabilIntegrado}
                        />
                    </>
            },
            {
                key: 'alinhamento-contas',
                ordem: 2,
                disabled: !(solicitacao?.etapasSolicitacaoAtivacao.find((e) => e.etapa.ordem === 1)?.concluidaEm),
                label: (
                    <EtapaTabTitle
                        numeroEtapa={2}
                        descricaoEtapa="Alinhamento de fontes e contas"
                        solicitacao={solicitacao}
                    />
                ),
                children:
                    <>
                        <AlinhamentoDeContasEFontes
                            solicitacao={solicitacao}
                            etapaAtiva={activeKeyTabs === 'alinhamento-contas'}
                            onUpdateOrgao={handleUpdateOrgao}
                            // TO-DO: Criar uma própria para cada ou alterar o nome posteriormente
                            onEtapaIniciada={handleEtapaIniciada}
                            onEtapaConcluida={handleEtapaIniciada}
                            onUpdateObservacao={handleEtapaIniciada}
                            onGetOrdenacaoFontes={handleGetOrdenacaoFontes}
                            onGetFontesOrfas={handleGetFontesOrfas}
                            onGetContasEPlanilha={handleGetContasEPlanilha}
                            onUpdateFonteFavorita={handleUpdateFonteFavorita}
                            onUpdateOrdenacaoFontes={handleUpdateOrdenacaoFontes}
                            onUpdateCategoriaFonte={handleUpdateCategoriaFonte}
                            onUpdateDescricaoFonte={handleUpdateDescricaoFonte}
                            onCreateFonte={handleCreateFonte}
                            onUpdateConta={handleUpdateConta}
                            onCreateConta={handleCreateConta}
                            onDeleteConta={handleDeleteConta}
                            onEncerrarConta={handleEncerrarConta}
                            onDeleteFonte={handleDeleteFonte}
                            onUpdateContasFontes={handleUpdateContasFontes}
                            onGetFontes={handleGetFontes}
                            onGetOrgaosVinculados={handleGetOrgaosVinculados}
                            recarregarContas={recarregarContas}
                            onContasRecarregadas={handleContasRecarregadas}
                        />
                    </>
            },
            {
                key: 'integracao-bancos',
                ordem: 3,
                disabled: !(solicitacao?.etapasSolicitacaoAtivacao.find((e) => e.etapa.ordem === 2)?.concluidaEm),
                label: (
                    <EtapaTabTitle
                        numeroEtapa={3}
                        descricaoEtapa="Integração com bancos"
                        solicitacao={solicitacao}
                    />
                ),
                children:
                    <>
                        <IntegracaoComBancos
                            solicitacao={solicitacao}
                            onUpdateOrgao={handleUpdateOrgao}
                            onEtapaIniciada={handleEtapaIniciada}
                            onEtapaConcluida={handleEtapaIniciada}
                            onUpdateObservacao={handleEtapaIniciada}
                            onGetAutorizacoesBancoDoBrasil={handleGetAutorizacoesBancoDoBrasil}
                            onGetContasComErro={handleGetContasComErro}
                            onGetContasInativas={handleGetContasInativas}
                            onCreateIdAplicacao={handleCreateIdAplicacao}
                            onUpdateIdAplicacao={handleUpdateIdAplicacao}
                            onCreateCredenciais={handleCreateCredenciais}
                            onUpdateCredenciais={handleUpdateCredenciais}
                            onDeleteAutorizacao={handleDeleteAutorizacao}
                            onDescobreCnpjContaComErro={handleDescobreCnpjContaComErro}
                            onGetContaById={handleGetContaById}
                            onUpdateConta={handleUpdateConta}
                            onContaCorrigida={handleContaCorrigida}
                            onGetBancosVanBancaria={handleGetBancosVanBancaria}
                            onUpdateNumeroRequerimentoVan={handleUpdateNumeroRequerimentoVan}
                            onCreateCredenciaisVan={handleCreateCredenciaisVan}
                            onUpdateCredenciaisVan={handleUpdateCredenciaisVan}
                            onGetDocumentoVanSignedUrl={handleGetDocumentoSignedUrlVan}
                        />
                    </>
            },
            {
                key: 'definicao-permissoes',
                ordem: 4,
                disabled: !(solicitacao?.etapasSolicitacaoAtivacao.find((e) => e.etapa.ordem === 3)?.concluidaEm),
                label: (
                    <EtapaTabTitle
                        numeroEtapa={4}
                        descricaoEtapa="Definição das permissões dos usuários"
                        solicitacao={solicitacao}
                    />
                ),
                children:
                    <>
                        <PermissoesUsuarios
                            solicitacao={solicitacao}
                            onUpdateOrgao={handleUpdateOrgao}
                            onEtapaIniciada={handleEtapaIniciada}
                            onEtapaConcluida={handleEtapaIniciada}
                            onUpdateObservacao={handleEtapaIniciada}
                            onGetUsuarios={handleGetUsuarios}
                            onUpdateCategoriasUsuario={handleUpdateCategoriasUsuario}
                        />
                    </>
            }
        ]
    }, [solicitacao, handleCreateOrgaoVinculado, handleUpdateOrgaoVinculado, handleDeleteOrgaoVinculado, requestUpdateSistemaContabil, handleDescobreCnpjContaComErro, handleGetContaById, handleUpdateConta, recarregarContas, handleContaCorrigida, handleContasRecarregadas, activeKeyTabs]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadSolicitacao() {
            try {
                setIsLoadingSolicitacao(true);

                const solicitacaoData = await SolicitacoesDeAtivacaoService.getSolicitacaoAtivacaoById({
                    id,
                    signal: controller.signal
                });

                setSolicitacao(solicitacaoData);

                const etapas = [...solicitacaoData.etapasSolicitacaoAtivacao];

                etapas.sort((a, b) => a.etapa.ordem - b.etapa.ordem);

                const etapaAtual = etapas.find((e) => !e.concluidaEm) || etapas[etapas.length - 1];

                setActiveKeyTabs(tabs.find((e) => e.ordem === etapaAtual.etapa.ordem)?.key || tabs[0].key);

                setIsLoadingSolicitacao(false);
            } catch (error) {
                EmitirErro(error, true, 'loadSolicitacao_a8sdasdd0sdad');
                setIsLoadingSolicitacao(false);
            }
        }

        loadSolicitacao();

        return () => {
            controller.abort();
        }
    }, []);

    return {
        tabs,
        onClickTab,
        activeKeyTabs,
        solicitacao,
        onClickVoltarListagem,
        valueDataConclusao,
        isLoadingSolicitacao,
    }
}
